// frontend/src/pages/Dashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider } from
'@mui/material';
import {
  LocationOn as LocationIcon,
  Devices as DevicesIcon,
  ConfirmationNumber as TicketIcon,
  TrendingUp as TrendingUpIcon,
  NewReleases as NewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowForwardIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon } from
'@mui/icons-material';
import { notificationsApi } from '../services/apiClient';
import { usePermissions } from '../contexts/PermissionContext';
import { useThemeSettings } from "../contexts/ThemeContext";
import { useSiteFilter } from '../contexts/SiteFilterContext';
import { StatsSkeleton } from '../components/SkeletonLoaders';
import ErrorMessage from '../components/ErrorMessage';
import DeviceService from '../services/deviceService';
import LocationService from '../services/locationService';
import TicketService from '../services/ticketService';

const Dashboard = () => {const { t } = useThemeSettings();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { selectedSiteId, siteLocationIds, hasSiteFilter, loading: siteFilterLoading } = useSiteFilter();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    locations: { total: 0 },
    devices: { total: 0, online: 0, offline: 0 },
    tickets: { total: 0, open: 0, inProgress: 0, closed: 0 }
  });
  const [error, setError] = useState(null);
  const [recentNotifications, setRecentNotifications] = useState([]);

  useEffect(() => {
    // Try to load from cache first (instant display of stale data)
    const loadCachedData = async () => {
      try {
        const cachedPromises = [];

        // Try to get cached locations (optimistic - let the service decide)
        if (hasPermission('locations.view')) {
          cachedPromises.push(
            LocationService.getStatistics({ useCache: true }).
            then((res) => res.data.total > 0 ? { locations: res.data } : null).
            catch(() => null)
          );
        }

        // Try to get cached devices (optimistic - let the service decide)
        if (hasPermission('devices.view')) {
          cachedPromises.push(
            DeviceService.getAllDevices({ useCache: true }).
            then((res) => {
              let devices = [];
              if (res?.data?.data && Array.isArray(res.data.data)) {
                devices = res.data.data;
              } else if (res?.data?.devices && Array.isArray(res.data.devices)) {
                devices = res.data.devices;
              } else if (res?.data && Array.isArray(res.data)) {
                devices = res.data;
              } else if (Array.isArray(res)) {
                devices = res;
              }

              if (devices.length > 0) {
                return {
                  devices: {
                    total: devices.length,
                    online: devices.filter((d) => {
                      const status = (d.status || d.config?.status || '').toLowerCase();
                      return ['online', 'active', 'registered'].includes(status);
                    }).length,
                    offline: devices.filter((d) => {
                      const status = (d.status || d.config?.status || '').toLowerCase();
                      return ['offline', 'inactive', 'disconnected'].includes(status);
                    }).length
                  }
                };
              }
              return null;
            }).
            catch(() => null)
          );
        }

        // Try to get cached tickets (optimistic - let the service decide)
        if (hasPermission('tickets.view')) {
          cachedPromises.push(
            TicketService.getStatistics({ useCache: true }).
            then((res) => res.data.total >= 0 ? { tickets: res.data } : null).
            catch(() => null)
          );
        }

        // If we have any cached data, show it immediately
        if (cachedPromises.length > 0) {
          const cachedResults = await Promise.all(cachedPromises);
          const filteredResults = cachedResults.filter(r => r !== null);
          if (filteredResults.length > 0) {
            const cachedStats = Object.assign({}, ...filteredResults);
            // Merge with default values
            setStats(prevStats => ({ ...prevStats, ...cachedStats }));
            setLoading(false);
            console.log('[Dashboard] Loaded from cache, refreshing in background...');
          }
        }
      } catch (err) {
        console.error('Error loading cached data:', err);
      }
    };

    // Load cached data first (if available)
    loadCachedData();

    // Then fetch fresh data in background (this will update the UI when ready)
    fetchDashboardData(false);
  }, [selectedSiteId, siteLocationIds]); // Refetch when site filter changes

  const fetchDashboardData = async (showLoader = true) => {
    try {
      // Only show loading skeleton on first load or when explicitly requested
      if (showLoader) {
        setLoading(true);
      }

      // Build site filter params
      const siteFilter = hasSiteFilter && siteLocationIds ? { locationIds: siteLocationIds } : {};

      const promises = [];

      // Fetch recent notifications
      promises.push(
        notificationsApi.getAll({ limit: 5, includeRead: false }).
        then((res) => {
          setRecentNotifications(res.data.notifications || []);
        }).
        catch(() => setRecentNotifications([]))
      );

      // Fetch locations if user has permission
      if (hasPermission('locations.view')) {
        promises.push(
          LocationService.getAllLocations({ useCache: !hasSiteFilter, forceRefresh: hasSiteFilter }).
          then((res) => {
            let locations = res?.data?.locations || res?.data?.data || res?.data?.docs || res?.data || [];

            // Filter by site if needed
            if (hasSiteFilter && siteLocationIds) {
              locations = locations.filter(loc =>
                siteLocationIds.includes(Number(loc.id)) || siteLocationIds.includes(String(loc.id))
              );
            }

            return { locations: { total: locations.length } };
          }).
          catch(() => ({ locations: { total: 0 } }))
        );
      } else {
        promises.push(Promise.resolve({ locations: { total: 0 } }));
      }

      // Fetch devices if user has permission
      if (hasPermission('devices.view')) {
        promises.push(
          DeviceService.getAllDevices({ useCache: !hasSiteFilter, forceRefresh: hasSiteFilter }).
          then((res) => {
            // Extract devices from response - handle multiple possible structures
            let devices = [];
            if (res?.data?.data && Array.isArray(res.data.data)) {
              devices = res.data.data;
            } else if (res?.data?.devices && Array.isArray(res.data.devices)) {
              devices = res.data.devices;
            } else if (res?.data && Array.isArray(res.data)) {
              devices = res.data;
            } else if (Array.isArray(res)) {
              devices = res;
            }

            // Filter by site if needed
            if (hasSiteFilter && siteLocationIds) {
              devices = devices.filter(device => {
                const deviceLocationId = device.locationId || device.location_id || device.location?.id;
                return siteLocationIds.includes(Number(deviceLocationId)) || siteLocationIds.includes(String(deviceLocationId));
              });
            }

            return {
              devices: {
                total: devices.length,
                online: devices.filter((d) => {
                  const status = (d.status || d.config?.status || '').toLowerCase();
                  return ['online', 'active', 'registered'].includes(status);
                }).length,
                offline: devices.filter((d) => {
                  const status = (d.status || d.config?.status || '').toLowerCase();
                  return ['offline', 'inactive', 'disconnected'].includes(status);
                }).length
              }
            };
          }).
          catch(() => ({ devices: { total: 0, online: 0, offline: 0 } }))
        );
      } else {
        promises.push(Promise.resolve({ devices: { total: 0, online: 0, offline: 0 } }));
      }

      // Fetch tickets if user has permission
      if (hasPermission('tickets.view')) {
        promises.push(
          TicketService.getAllTickets({ useCache: !hasSiteFilter }).
          then((res) => {
            let tickets = res?.data?.tickets || res?.data?.data || res?.data || [];

            // Filter by site if needed
            if (hasSiteFilter && siteLocationIds) {
              tickets = tickets.filter(ticket => {
                const ticketLocationId = ticket.locationId || ticket.location_id || ticket.location?.id;
                return siteLocationIds.includes(Number(ticketLocationId)) || siteLocationIds.includes(String(ticketLocationId));
              });
            }

            return {
              tickets: {
                total: tickets.length,
                open: tickets.filter(t => t.status === 'open').length,
                inProgress: tickets.filter(t => t.status === 'in_progress' || t.status === 'inProgress').length,
                closed: tickets.filter(t => t.status === 'closed' || t.status === 'resolved').length
              }
            };
          }).
          catch(() => ({ tickets: { total: 0, open: 0, inProgress: 0, closed: 0 } }))
        );
      } else {
        promises.push(Promise.resolve({ tickets: { total: 0, open: 0, inProgress: 0, closed: 0 } }));
      }

      const results = await Promise.all(promises);
      const combinedStats = Object.assign({}, ...results);
      setStats(combinedStats);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  const statCards = [
  {
    title: 'Locations',
    value: stats.locations.total,
    icon: LocationIcon,
    color: '#1976d2',
    path: '/locations',
    permission: 'locations.view'
  },
  {
    title: 'Devices',
    value: stats.devices.total,
    subtitle: `${stats.devices.online} online • ${stats.devices.offline} offline`,
    icon: DevicesIcon,
    color: '#388e3c',
    path: '/devices',
    permission: 'devices.view'
  },
  {
    title: 'Open Tickets',
    value: stats.tickets.open + stats.tickets.inProgress,
    subtitle: `${stats.tickets.open} open • ${stats.tickets.inProgress} in progress`,
    icon: TicketIcon,
    color: '#f57c00',
    path: '/tickets',
    permission: 'tickets.view'
  },
  {
    title: 'Closed Tickets',
    value: stats.tickets.closed,
    icon: CheckCircleIcon,
    color: '#7b1fa2',
    path: '/tickets',
    permission: 'tickets.view'
  }];


  const whatsNew = [
    {
      version: 'v260127a',
      releaseNotes: '/release-notes/v260127a',
      date: '27/01/2026',
      items: [
        '⌨️ Keyboard Shortcuts - Sneltoetsen voor tickets (C, /, F, R, ↑↓, Esc)',
        '✏️ Inline Bewerking - Locatie, apparaat en volgende stap direct aanpassen',
        '📋 Next Step Kolom - Zie de volgende actie in de ticketlijst',
        '🔍 Server-side Zoeken - Doorzoek alle tickets'
      ]
    },
    {
      version: 'v251217x',
      releaseNotes: '/release-notes/v251217x',
      date: '17/12/2025',
      items: [
        '🏢 Site Switcher - Snel wisselen tussen locaties',
        '🔒 Site Toegangsbeheer - Wijs sites toe aan gebruikers',
        '🔍 Automatische filtering op alle pagina\'s',
        '🛠️ Bug fixes en performance verbeteringen'
      ]
    },
    {
      version: 'v2510028a',
      releaseNotes: '/release-notes/v2510028a',
      date: '28/10/2025',
      items: [
      '🛠️ Bug fixes and performance improvements',
      // Camera requests
      '📸 Camera Footage Requests - Vraag eenvoudig camerabeelden aan via CARO'
      // Interventieverslagen bij fysieke interventies
    , '📝 Interventieverslagen bij fysieke interventies',
     // verbeterde ticket zoekfunctie om locaties te vinden
     '🔍 Verbeterde ticket zoekfunctie om locaties te vinden'
    ]
  },
  {
    version: 'v251007a',
    releaseNotes: '/release-notes/v251007a',
    date: '07/10/2025',
    items: [
    '🔍 Krachtige wildcard zoeken met * joker token',
    '🔎 Zoek op apparaatnaam, maker en email',
    '✨ Vloeiende animaties door heel CARO',
    '📚 Help en Developer Info pagina\'s toegevoegd',
    'ℹ️ Interactieve zoek tooltip met voorbeelden',
    '⚡ 60fps GPU-accelerated animaties']

  },
  {
    version: 'v251006c',
    releaseNotes: '/release-notes/v251006c',
    date: '06/10/2025',
    items: [
    '🌍 Multi-Language Support - CARO spreekt nu jouw taal!',
    '🇳🇱 Nederlands, 🇬🇧 English, 🇫🇷 Français, 🇩🇪 Deutsch',
    '🟡 West-Vlaams en 💎 Antwerps dialecten toegevoegd',
    '⚡ Real-time taal switching zonder pagina herlaad',
    '📅 Localized datum en tijd formaten per regio',
    '🔧 Ga naar Instellingen > Taal & Regio om te wijzigen']

  },
  {
    version: 'v251006b',
    releaseNotes: '/release-notes/v251006b',
    date: '06/10/2025',
    items: [
    '📱 Complete Mobile & Tablet Overhaul - Geen horizontaal scrollen meer!',
    '⚡ Quick Actions Dashboard voor snelle toegang tot functies',
    '📋 Tabellen → Kaarten op mobiel voor betere leesbaarheid',
    '👆 Touch-geoptimaliseerde knoppen en interface',
    '📐 Volledige schermweergave voor pop-ups op mobiel',
    '🎯 Menu\'s passen zich automatisch aan (tabs → accordions)',
    '✅ Getest op alle apparaten: iPhone, Android, iPad, tablets']

  },
  {
    version: 'v2.1.0',
    releaseNotes: '/release-notes/v251003',
    date: '03/01/2025',
    items: [
    '🎯 Live ticketing systeem - Meld problemen en volg de status',
    '📊 Real-time dashboard met statistieken',
    '🏢 Locatiebeheer met hiërarchische structuur',
    '💻 Apparaatbeheer met online/offline status',
    '📱 Progressive Web App (PWA) - Installeer als desktop/mobiele app',
    '🔐 Microsoft SSO met Multi-Factor Authenticatie',
    '⚙️ Uitgebreide instellingen - Thema\'s, lettertypen en toegankelijkheid',
    '👥 Gebruikersbeheer met rol- en permissie systeem',
    '🌐 Multi-tenant architectuur per bedrijf',
    '📧 E-mail notificaties voor ticket updates',
    '💬 Commentaar en communicatie binnen tickets']

  },
  {
    version: 'Volgende Week',
    date: 'Week van 10/11/2025',
    items: [
    '📈 Uitgebreide apparaat informatie en historische data'
  ]

  }];


  if (loading) {
    return (
      <Box
        className="page-transition-enter"
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxSizing: 'border-box'
        }}
      >
        <Typography className="slide-down" variant="h4" sx={{ mb: 3 }}>
          {t ? t("Dashboard.dashboard") : "Dashboard"}
        </Typography>
        <StatsSkeleton />
      </Box>
    );
  }

  return (
    <Box
      className="page-transition-enter"
      sx={{
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography className="slide-down" variant="h4">{t ? t("Dashboard.dashboard") : "Dashboard"}</Typography>
        <Button
          size="small"
          startIcon={<TrendingUpIcon />}
          onClick={() => fetchDashboardData()}>{t ? t("DeviceStatusDashboard.refresh") : "Refresh"}


        </Button>
      </Box>

      {error && (
        <ErrorMessage
          error={error}
          title={t ? t("Dashboard.error_loading_data") : "Error Loading Dashboard"}
          context="dashboard"
          onRetry={fetchDashboardData}
          onClose={() => setError(null)}
          showDetails={true}
        />
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {statCards.map((stat, index) => {
          const canView = !stat.permission || hasPermission(stat.permission);
          if (!canView) return null;

          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card className="card-animated card-enter"
                sx={{
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
                onClick={() => navigate(stat.path)}>
                
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="textSecondary" variant="body2" gutterBottom>
                        {stat.title}
                      </Typography>
                      <Typography variant="h3" component="div" sx={{ mb: 1 }}>
                        {stat.value}
                      </Typography>
                      {stat.subtitle &&
                      <Typography variant="caption" color="textSecondary">
                          {stat.subtitle}
                        </Typography>
                      }
                    </Box>
                    <stat.icon sx={{ fontSize: 40, color: stat.color, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>);

        })}
      </Grid>

      <Grid container spacing={3}>
        {/* Recent Notifications Widget */}
        {recentNotifications.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <NotificationsIcon color="primary" />
                  {t ? t('Recent Notifications') : 'Recent Notifications'}
                </Typography>
                <Button
                  size="small"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate('/notifications')}
                >
                  {t ? t('View All') : 'View All'}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {recentNotifications.slice(0, 3).map((notification) => (
                  <Alert
                    key={notification.id}
                    severity={
                      notification.priority === 'critical' || notification.priority === 'urgent'
                        ? 'error'
                        : notification.priority === 'high'
                        ? 'warning'
                        : 'info'
                    }
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      if (notification.actionUrl) {
                        navigate(notification.actionUrl);
                      } else {
                        navigate('/notifications');
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {notification.title}
                      </Typography>
                      <Typography variant="body2">
                        {notification.body}
                      </Typography>
                    </Box>
                  </Alert>
                ))}
              </Box>
            </Paper>
          </Grid>
        )}

        {/* Quick Actions */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon color="primary" />{t ? t("LocationDetails.quick_actions") : "Quick Actions"}

            </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="outlined"
                startIcon={<LocationIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/locations')}
                fullWidth
                sx={{ justifyContent: 'space-between' }}>{t ? t("Dashboard.manage_locations") : "Manage Locations"}


              </Button>
              <Button
                variant="outlined"
                startIcon={<DevicesIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/devices')}
                fullWidth
                sx={{ justifyContent: 'space-between' }}>{t ? t("Dashboard.view_devices") : "View Devices"}


              </Button>
              
              <Button
                variant="outlined"
                startIcon={<TicketIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/tickets')}
                fullWidth
                sx={{ justifyContent: 'space-between' }}>{t ? t("Dashboard.view_tickets") : "View Tickets"}


              </Button>
              
              <Button
                variant="contained"
                startIcon={<TicketIcon />}
                onClick={() => navigate('/tickets?action=create')}
                fullWidth
                color="primary">{t ? t("Dashboard.create_new_ticket") : "Create New Ticket"}


              </Button>
                <Button
                variant="outlined"
                startIcon={<SettingsIcon />}
                endIcon={<ArrowForwardIcon />}
                onClick={() => navigate('/settings')}
                fullWidth
                sx={{ justifyContent: 'space-between', mt: 2 }}>{t ? t("Dashboard.personaliseer_caro") : "Personaliseer CARO"}


              </Button>
              </Box>
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <SettingsIcon fontSize="small" />{t ? t("Dashboard.pas_kleuren_lettertypen_en_toegankelijkh") : "Pas kleuren, lettertypen en toegankelijkheid aan in Instellingen"}

              </Typography>
              </Box>

              {/* Features in Near Future */}
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <NewIcon color="secondary" />{t ? t("Dashboard.features_in_near_future") : "Features in Near Future"}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {whatsNew.filter(release => release.version.includes('Volgende Week')).map((release, idx) =>
                <Box key={idx}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label={release.version} size="small" color="secondary" />
                      <Typography variant="caption" color="textSecondary">
                        {release.date}
                      </Typography>
                    </Box>
                    <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                      {release.items.map((item, itemIdx) =>
                    <Typography
                      component="li"
                      variant="body2"
                      color="textSecondary"
                      key={itemIdx}
                      sx={{ mb: 0.5 }}>

                          {item}
                        </Typography>
                    )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>

        {/* What's New */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }} className="card-animated">
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <NewIcon color="primary" />{t ? t("Dashboard.whats_new") : "What's New?"}

            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {whatsNew.filter(release => !release.version.includes('Volgende Week')).map((release, idx) =>
              <Box key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={release.version}
                      size="small"
                      color="primary"
                      onClick={release.releaseNotes ? () => window.open(release.releaseNotes, '_blank', 'noopener,noreferrer') : undefined}
                      sx={release.releaseNotes ? { cursor: 'pointer', '&:hover': { opacity: 0.8 } } : {}}
                    />
                    <Typography variant="caption" color="textSecondary">
                      {release.date}
                    </Typography>
                    {release.releaseNotes && (
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => window.open(release.releaseNotes, '_blank', 'noopener,noreferrer')}
                        sx={{ ml: 'auto', minWidth: 'auto', px: 1 }}
                      >
                        View Notes
                      </Button>
                    )}
                  </Box>
                  <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                    {release.items.map((item, itemIdx) =>
                  <Typography
                    component="li"
                    variant="body2"
                    color="textSecondary"
                    key={itemIdx}
                    sx={{ mb: 0.5 }}>

                        {item}
                      </Typography>
                  )}
                  </Box>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>);

};

export default Dashboard;